// src/FAQ.js
// ============================================================
// Your Scholarship Finder — Member FAQ / Help Page
// ============================================================
// 22 Q&As organized in 5 sections. Accessible from AppHeader.
// Content sourced from Matt's YSF_Member_FAQ.docx (Session 34).
// Items marked TODO need confirmation before public launch.
// ============================================================

import { useState } from 'react';
import AppHeader from './AppHeader';

// ── Accordion Item ──────────────────────────────────────────
function FAQItem({ question, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={s.item}>
      <button onClick={() => setOpen(!open)} style={s.question} aria-expanded={open}>
        <span style={s.qText}>{question}</span>
        <span style={s.chevron}>{open ? '−' : '+'}</span>
      </button>
      {open && <div style={s.answer}>{children}</div>}
    </div>
  );
}

// ── Section Header ──────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={s.section}>
      <h2 style={s.sectionTitle}>{title}</h2>
      {children}
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────
export default function FAQ() {
  return (
    <div style={s.page}>
      <AppHeader subtitle="Help & FAQ" />
      <div style={s.container}>
        <h1 style={s.pageTitle}>Frequently Asked Questions</h1>
                <p style={s.intro}>
          We have compiled answers to the most frequently asked questions when it comes to using
          the Your Scholarship Finder platform. Search below for your answer, and if you can't
          find it still,{' '}
          <a href="mailto:theteam@yourscholarshipfinder.com" style={s.link}>
            we can help
          </a>
          .
        </p>

        {/* ─── GETTING STARTED & LOGINS ─────────────────── */}
        <Section title="Getting Started & Logins">

          <FAQItem question="My student never got their login email. What do I do?">
            <p>Almost always one of three things. Work through these in order:</p>
            <p><strong>Check spam, junk, and the Promotions tab.</strong> Our emails come from theteam@yourscholarshipfinder.com — add that address to your contacts so future emails land in the inbox.</p>
            <p><strong>Confirm the email address is right.</strong> Log in to your parent dashboard and check your student's email for typos. Correcting it will trigger a new invitation.</p>
            <p><strong>Still nothing?</strong> Email us at theteam@yourscholarshipfinder.com and we'll set it up manually.</p>
                    </FAQItem>
          <FAQItem question="My student's password setup link expired. What do I do?">
            <p>No problem — the link we send is good for 7 days. If it expires, your student can set their password in a few seconds:</p>
            <p>Go to <a href="https://www.yourscholarshipfinder.com/login" style={s.link}>www.yourscholarshipfinder.com</a> and click <strong>"Forgot your password?"</strong> below the login form. Enter your student's email address and they'll get a fresh link right away.</p>
            <p>Their profile and matches are safe — nothing is lost when a link expires.</p>
          </FAQItem>
          <FAQItem question="Does my student need their own login, or can we share one account?">
            <p>Your student should have their own login. Two reasons:</p>
            <p><strong>The tracking only works if they use it.</strong> Your student updates the status of each scholarship as they start, submit, and win. That's what feeds your parent dashboard — if they're logged in as you, there's nothing for you to see.</p>
            <p><strong>It has to be theirs.</strong> If the account lives with you, the scholarship search stays your project. The whole point is that your student runs it and you keep an eye on progress.</p>
            <p>You'll each have your own login. Yours shows the parent dashboard; theirs shows the matches and the applications.</p>
          </FAQItem>

          <FAQItem question="How do I reset my password?">
            <p>Go to <a href="https://www.yourscholarshipfinder.com/login" style={s.link}>www.yourscholarshipfinder.com</a> and select <strong>Log In</strong>.</p>
            <p>Click <strong>"Forgot your password?"</strong> below the login form.</p>
            <p>Enter the email address on your account and submit.</p>
            <p>Check your email for a reset link. It's good for <strong>one hour</strong>. Look in spam if it isn't there within a few minutes.</p>
            <p>Follow the link and choose a new password.</p>
            <p>Your student resets theirs the same way, using their own email address.</p>
          </FAQItem>

          {/* TODO: Confirm whether username and login email are the same field,
              and whether a change requires re-verification. Currently no
              "change email" feature exists in the app. */}
          <FAQItem question="How do I change my email address?">
            <p>Email us at <a href="mailto:theteam@yourscholarshipfinder.com" style={s.link}>theteam@yourscholarshipfinder.com</a> and we'll update it for you.</p>
            <p>Changing your email address changes where our emails go, including receipts and renewal notices — so make sure it's an address you check.</p>
          </FAQItem>

        </Section>

        {/* ─── PROFILE & MATCHES ────────────────────────── */}
        <Section title="Profile & Matches">

          <FAQItem question="How do I update my profile?">
            <p>Log in at <a href="https://www.yourscholarshipfinder.com" style={s.link}>www.yourscholarshipfinder.com</a>.</p>
            <p>Click your <strong>profile</strong> in the top right corner.</p>
            <p>Update anything that's changed — grades, activities, location, intended major, the college you've committed to.</p>
            <p>Save. Your matches will refresh automatically — this takes a few minutes while our AI re-evaluates every scholarship against your updated profile.</p>
            <p>This is the most important thing in the whole platform. Every match comes from what's in the profile, so an out-of-date profile produces an out-of-date list.</p>
          </FAQItem>

          <FAQItem question="How often should we update it?">
            <p>Any time something changes. In practice that means:</p>
            <p>A new activity, job, club, sport, or volunteer role — each one can unlock awards your student wasn't eligible for last semester.</p>
            <p>A GPA or test score change.</p>
            <p>A change in intended major.</p>
            <p>Committing to a college.</p>
            <p>Moving.</p>
            <p>A quick look once a month is plenty. If a list suddenly seems thin, the profile is the first place to check.</p>
          </FAQItem>

          <FAQItem question="My student committed to a college. What needs to change?">
            <p>Update the profile the same day, and don't wait until summer. A lot of awards are restricted to students attending a specific college, program, or state system, and those won't appear in your student's results until the profile says where they're going.</p>
            <p>Log in and open the profile from the top right. Enter the college your student will attend. Confirm the intended major is still accurate while you're in there. Save, and have your student look at their refreshed list — it usually changes noticeably.</p>
          </FAQItem>

          <FAQItem question="What if my student changes their intended major?">
            <p>Update it. This happens constantly and it's not a problem — but it does matter, because major-specific awards are some of the most winnable ones on the list. A profile that still says biology when your student switched to business is producing the wrong list.</p>
            <p>Same path: log in, profile in the top right, change the major, save.</p>
            {/* TODO: Determine guidance for undecided students. Is there an
                "undecided" option in the major field, and how does it affect matching? */}
            <p>If your student is genuinely undecided, leave the major field blank for now. You can always update it later, and scholarships that don't require a specific major will still appear.</p>
          </FAQItem>

          <FAQItem question="How do I add another student?">
            {/* TODO: Confirm the family plan student cap (currently unlimited
                in the code — checkProfileLimit only blocks individual plans).
                Also confirm upgrade billing: prorated or charged in full. */}
            <p>The Family plan covers multiple students. Each one gets their own profile, their own login, and their own set of matches — they don't share a list, since a sophomore and a college junior are looking at completely different awards.</p>
            <p>Log in and go to your <strong>Parent Dashboard</strong>. Click <strong>"Add Student"</strong> and enter your student's name and email address. Your student gets their own login email and sets their own password. Have them complete their profile — matches don't generate until it's done.</p>
            <p>On the Individual plan? You can upgrade to Family anytime from your <strong>Account</strong> page.</p>
            <p>And it's worth adding younger students now rather than waiting. Scholarship deadlines run year-round, plenty of awards are open to sophomores and juniors, and some are renewable — meaning one win can follow them for four years.</p>
          </FAQItem>

        </Section>

        {/* ─── FINDING & APPLYING ───────────────────────── */}
        <Section title="Finding & Applying">

          <FAQItem question="How do I actually apply to a scholarship?">
            <p>Open the scholarship in your results and read the match explanation and the requirements.</p>
            <p>Click the <strong>Apply</strong> button next to that scholarship. It takes you straight to the provider's own website.</p>
            <p>Complete the application there — the provider handles the form, the essay, and the submission.</p>
            <p>Come back and update the <strong>status dropdown</strong> next to that scholarship so your list stays accurate.</p>
            <p>That last step takes two seconds and is the one people skip. Without it you'll re-read the same award next month wondering whether you already applied.</p>
          </FAQItem>

          <FAQItem question="I found a broken link in an application. What do I do?">
            <p>Tell us — that's a real help, and we'd rather hear about it than have you assume it's you.</p>
            <p>Email us at <a href="mailto:theteam@yourscholarshipfinder.com" style={s.link}>theteam@yourscholarshipfinder.com</a> with the scholarship name and we'll look into it.</p>
            <p>In the meantime, search the scholarship name plus the sponsoring organization. Providers move application pages more often than you'd think, and the award is usually still open at a new address.</p>
            <p>If the deadline is close, apply through the page you find and don't wait on us.</p>
            <p>A broken link sometimes means the award closed or the program ended. We check every report and either fix the link or remove the listing.</p>
          </FAQItem>

          <FAQItem question="How do I track what we've started, submitted, and won?">
            <p>Every scholarship in your student's results has a <strong>status dropdown</strong> next to it. Your student updates it as they go:</p>
            <p><strong>Saved</strong> — bookmarked for later.</p>
            <p><strong>In Progress</strong> — working on the application (set automatically when they click Apply).</p>
            <p><strong>Submitted</strong> — application sent.</p>
            <p><strong>Awarded</strong> — they won it.</p>
            <p>Those updates are what populate the parent dashboard. The system can't detect a submission on a provider's website, so if your student doesn't update the dropdown, the numbers stay at zero no matter how much work they've done.</p>
          </FAQItem>

          <FAQItem question="Do you submit applications for us?">
            <p>No. We find the awards your student qualifies for, explain why each one matched, and take you to the application — but your student submits it themselves on the provider's site.</p>
            <p>That's not a limitation we're working around. Scholarship applications require essays in the student's own voice, transcripts, recommendation letters, and in most cases the student's own signature affirming the work is theirs. A service that submitted on their behalf would disqualify them.</p>
          </FAQItem>

          <FAQItem question="What happens if we miss a deadline?">
            <p>That one's closed for this cycle — providers don't accept late applications, and there's nothing we can do to reopen it.</p>
            <p>The better news is that most scholarships run annually. If your student is still eligible next year, the same award will usually come around again, and it'll reappear in their matches when it opens.</p>
            <p>To avoid the next one: have your student set their own deadline two days before the real one. Portals go down, uploads fail, and transcript requests take longer than anyone expects.</p>
          </FAQItem>

        </Section>

        {/* ─── FOR PARENTS ──────────────────────────────── */}
        <Section title="For Parents">

          <FAQItem question="What does the parent dashboard show me?">
            <p>Four things:</p>
            <p><strong>Total matches</strong> — the scholarships your student actually qualifies for.</p>
            <p><strong>Excluded matches</strong> — awards that were screened out (see the next question).</p>
            <p><strong>Applications started, submitted, and won</strong> — based on what your student marks in their status dropdown.</p>
            <p><strong>Links to their results</strong>, so you can look through the list yourself.</p>
            <p>The point is that you can see where things stand without asking. No status updates required, no "did you do it yet" at dinner.</p>
            <p>One caveat worth knowing: the application numbers only move when your student updates their dropdown. A zero can mean nothing has been submitted, or it can mean they've been applying and not marking it. Ask before you assume.</p>
          </FAQItem>

          <FAQItem question='What does "excluded matches" mean?'>
            <p>Excluded matches are scholarships we looked at and ruled out for your student — wrong state, wrong major, GPA cutoff, an eligibility requirement they don't meet. Our AI explains exactly why each one was excluded, so you can see the reasoning.</p>
            <p>A high excluded number is a good sign. It means our system evaluated thousands of scholarships on your behalf and filtered out the ones that don't apply. That's the haystack your student didn't have to dig through.</p>
          </FAQItem>

          <FAQItem question="Can my student see what I can see?">
            <p>Your student sees their matches, deadlines, application links, and their own tracking. The parent dashboard is your view — it summarizes their activity so you don't have to ask for updates.</p>
            <p>We'd suggest telling your student what you can see rather than leaving it a surprise. Nothing sours this faster than a student who thinks they're being monitored without knowing it. It's a collaboration tool, not a surveillance one.</p>
          </FAQItem>

        </Section>

        {/* ─── MEMBERSHIP & BILLING ─────────────────────── */}
        <Section title="Membership & Billing">

          <FAQItem question="When does my membership renew?">
            {/* TODO: Set the advance-notice window for renewal emails.
                Auto-renewal reminder emails depend on #53 (annual renewal logic). */}
            <p>One year from the day you joined, and automatically each year after that. The exact date is on your purchase receipt, and we'll email you before the charge so it's never a surprise.</p>
          </FAQItem>

          <FAQItem question="Does membership keep working after my student starts college?">
            <p>Yes. Scholarships don't stop at high school graduation — college students qualify for awards every year, and plenty of them draw very few applicants because most families have stopped looking.</p>
            <p>Have your student update their profile once they're enrolled — college, year, and major — so their matches reflect where they actually are.</p>
          </FAQItem>

          <FAQItem question="How do I update my payment method?">
            <p>Log in and go to <strong>Account</strong> in the top right.</p>
            <p>Select <strong>Manage Subscription</strong>. This opens your billing portal where you can update your card details.</p>
            {/* TODO: Confirm the failed-payment grace period. */}
            <p>If a renewal payment fails, we'll email you — your access continues while you sort it out.</p>
          </FAQItem>

                    <FAQItem question="Can I get a refund?">
            <p>If Your Scholarship Finder isn't what you expected, email <a href="mailto:theteam@yourscholarshipfinder.com" style={s.link}>theteam@yourscholarshipfinder.com</a> within 7 days of your purchase for a full refund — no form to fill out and no reason required.</p>
            <p>After 7 days, memberships are non-refundable, but you can cancel anytime to stop your next renewal and keep full access through the end of your paid year.</p>
          </FAQItem>

                    <FAQItem question="How do I cancel my membership?">
            <p>Log in and go to <strong>Account</strong> in the top right.</p>
            <p>Select <strong>Manage Subscription</strong>, then click <strong>Cancel</strong>.</p>
            <p>That's it — no phone call, no long form.</p>
            <p>Your access continues through the end of the period you've already paid for, and you won't be charged again.</p>
          </FAQItem>

          <FAQItem question="Can I switch between plans?">
            <p>You can upgrade from Individual to Family at any time. We prorate the difference for the time remaining on your subscription — you only pay the gap, and access to add more children is immediate.</p>
            <p>To downgrade from Family to Individual, email <a href="mailto:theteam@yourscholarshipfinder.com" style={s.link}>theteam@yourscholarshipfinder.com</a> and we'll help you make the switch. You'll choose which child retains access.</p>
          </FAQItem>

        </Section>

        {/* ─── STILL STUCK ──────────────────────────────── */}
        <div style={s.stillStuck}>
          <h2 style={s.stillStuckTitle}>Still stuck?</h2>
          <p style={s.stillStuckText}>
            Email{' '}
            <a href="mailto:theteam@yourscholarshipfinder.com" style={s.link}>
              theteam@yourscholarshipfinder.com
            </a>{' '}
            or reply to any email we've sent. It reaches Matt and Ramesh directly, and we answer everything.
          </p>
        </div>

      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh',
    background: '#F7F9FB',
  },
  container: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '32px 24px 64px',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: '#10314F',
    margin: '0 0 8px',
  },
  intro: {
    fontSize: 15,
    color: '#555',
    lineHeight: 1.6,
    margin: '0 0 32px',
  },
  link: {
    color: '#1F4E79',
    fontWeight: 600,
    textDecoration: 'underline',
  },

  // Sections
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1F4E79',
    margin: '0 0 12px',
    paddingBottom: 8,
    borderBottom: '2px solid #E0E8F0',
  },

  // Accordion items
  item: {
    borderBottom: '1px solid #E8ECF0',
  },
  question: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 0',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    gap: 12,
  },
  qText: {
    fontSize: 15,
    fontWeight: 600,
    color: '#1A2A3A',
    lineHeight: 1.4,
  },
  chevron: {
    fontSize: 20,
    fontWeight: 500,
    color: '#1F4E79',
    flexShrink: 0,
    width: 24,
    textAlign: 'center',
  },
  answer: {
    padding: '0 0 16px',
    fontSize: 14,
    color: '#3A4A5A',
    lineHeight: 1.7,
  },

  // Still stuck
  stillStuck: {
    marginTop: 40,
    padding: '24px 28px',
    background: '#EDF2F7',
    borderRadius: 10,
    textAlign: 'center',
  },
  stillStuckTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#10314F',
    margin: '0 0 8px',
  },
  stillStuckText: {
    fontSize: 14,
    color: '#3A4A5A',
    lineHeight: 1.6,
    margin: 0,
  },
};
